import { Point2D, Point3D, PointType } from './answers/shape-typings';
import { FieldFeedbackExternal } from './field-feedback';

export interface ShapeFeedback {
  stepId?: number;
  outputId: number;
  feedbackId: string;
  errorTag: string;
  shapeId?: number;
  comment?: string;
  startFrame?: number;
  endFrame?: number;
  coordinates?: Point2D | Point3D;
  updatedAt: string;
  createdAt: string;
  tags?: { [key: string]: unknown };
  itemType: 'shape_feedback';
}

export type ObjectFeedbackType = 'highlight';

interface ObjectFeedback {
  feedbackId: string;
  objectId: number;
  stepId: number;
  createdBy: number;
  itemType: 'object_feedback' | 'object_feedback_metadata';
  itemSubType: ObjectFeedbackType;

  errorTag?: string;
  comment?: string;

  tagName?: string;
  sampleId?: number;
  readOnly?: boolean;

  createdAt?: string;
  updatedAt?: string;
}

export interface HighlightFeedback extends ObjectFeedback {
  itemSubType: 'highlight';
  tagName: 'text';
  errorTag: HighlightFeedbackErrorType;
  startIndex: number;
  endIndex: number;
  wordCount: number;

  // automated feedback fields
  valueBefore?: string;
  valueAfter?: string;
}

export function isHighlightFeedback(
  feedback: HighlightFeedback | ShapeFeedback | FieldFeedbackExternal,
): feedback is HighlightFeedback {
  return (
    feedback.itemType === 'object_feedback' &&
    'itemSubType' in feedback &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- object feedback will also have itemSubType but we don't have the interface yet
    feedback.itemSubType === 'highlight'
  );
}

export interface ClientFeedback {
  id: string;
  type: ClientFeedbackType;
  status: ClientFeedbackStatus;
  title: string;
  description: string;
  tags: string[];
  parkTasks: boolean;
  location: {
    frame_number: number;
    coordinates: PointType;
    output_id: number;
  };
  attachments: Attachment[];
  createdAt?: string;
  updatedAt: string;
  createdBy?: number;
  comments: ClientFeedbackComment[];
}

export interface Attachment {
  filename: string;
  type: string;
  s3ObjectKey: string;
  preSignedUrl?: string;
}

export interface ClientFeedbackComment {
  id?: string;
  text: string;
  issueId: string;
  isSolution: boolean;
  createdAt: string;
  attachments?: Attachment[];
}

export type ClientFeedbackStatus = 'new' | 'open' | 'closed';
export type ClientFeedbackType =
  | 'instruction-gap'
  | 'subjectivity'
  | 'sensor-issue'
  | 'edge-case'
  | 'other';

// We assume that the list is sorted alphabetically
export const highlightFeedbackErrorTypes = [
  'Factuality',
  'Instruction Following',
  'Language',
] as const;
export type HighlightFeedbackErrorType =
  (typeof highlightFeedbackErrorTypes)[number];

export function isHighlightFeedbackErrorType(
  value: string,
): value is HighlightFeedbackErrorType {
  return highlightFeedbackErrorTypes.includes(value as any);
}
